<template>
    <b-card
        :class="['list-container']"
        bg-variant="white"
        no-body
    >
        <template #header>
            <div class="d-flex align-items-center justify-content-between" style="height: 36px">
                <div >
                    <h6 class="mb-0">평가 참여 정보</h6>
                </div>
            </div>
        </template>
        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
        <b-table :items="selected_company.projects"
                 :fields="tableColumns"
                 :empty-html="getEmptyTableHTML"
                 :sticky-header="true"
                 class="mb-0 sticky-header-sort-fix custom-table"
                 table-class="ds-table ds-table-fixed company-project-table"
                 show-empty
        >

            <template #empty="">
                <div class="h6 gray-500 font-weight-normal text-center mb-0 py-3">
                    참여중인 프로젝트가 없습니다.
                </div>
            </template>

            <!-- 메인 칸 = 진행현황 링크(옛 '확인하기' 버튼 자리 · 새 탭 · 링크 복사가 된다). 참여기간은 같은 줄에 흐리게 -->
            <template #cell(project_code)="data">
                <span class="ds-ellipsis" :title="[data.value, data.item.period].filter(Boolean).join(' · ')"><router-link :to="progressRoute(data.item)" class="ds-detail-link">{{ data.value }}</router-link> <span class="ds-sub">{{ data.item.period }}</span></span>
            </template>

            <template #cell(client)="data">
                <span class="ds-ellipsis" :title="data.value">{{ data.value }}</span>
            </template>

            <!-- 응답에 캠페인 이름이 없어 평가산업이 그 자리 — 캠페인 ID 는 흐리게 -->
            <template #cell(industry_name)="data">
                <span class="ds-ellipsis" :title="`${data.value} (${data.item.campaign_id})`">{{ data.value }} <span class="ds-sub">{{ data.item.campaign_id }}</span></span>
            </template>

            <template #cell(status_code)="data">
                <span class="ds-status" :class="`ds-status-${getProgressTone(data.item)}`">{{ progressLabel(data.item) }}</span>
            </template>

            <template #head(member)="data">
                <span class="sr-only">{{ data.label }}</span>
            </template>

            <!-- 행 액션(아이콘 28): 참여자 창 · 진행현황(프로젝트 링크와 같은 곳) -->
            <template #cell(member)="data">
                <div class="d-flex justify-content-end" style="gap: 4px">
                    <!-- 조회 중에는 다른 행도 막아 늦게 온 응답이 다른 프로젝트 창을 덮지 않게 한다 -->
                    <button type="button" class="ds-row-icon-btn"
                            :disabled="loadingRequestId !== null"
                            :aria-label="`${data.item.project_code} 참여자`" :title="`${data.item.project_code} 참여자`"
                            @click="manageProjectMember(data.item)">
                        <b-spinner v-if="loadingRequestId === data.item.request_id" small/>
                        <AppIcon v-else name="users"/>
                    </button>
                    <router-link :to="progressRoute(data.item)" class="ds-row-icon-btn"
                                 :aria-label="`${data.item.project_code} 진행현황`" :title="`${data.item.project_code} 진행현황`">
                        <AppIcon name="chevron-right"/>
                    </router-link>
                </div>
            </template>
        </b-table>


        <AssessmentMemberModal ref="assessment-member-modal"
                               :selected_company="selected_company"
                               :read-only="readOnly"
                               @closed="onMemberModalClosed"
        />
    </b-card>
</template>


<script>
import assessmentCommonMixin from '@shared/mixin/assessment.common.mixin';
import { PROGRESS_STATUS_LABELS } from '@shared/components/admin/pages/Management/Company/data';
import {getEmptyTableHTML} from '@shared/components/admin/pages/Management/common-data';
import AssessmentMemberModal from './AssessmentMemberModal.vue';

// DS 표 규칙: 메인(프로젝트 · 참여기간) → 데이터 → 행 아이콘. 폭 없는 칸(프로젝트 · 캠페인)이 남는 폭을 나눠 갖는다
const columns = [
    {key: 'project_code', label: '프로젝트'},
    {key: 'client', label: '원청사', thStyle: {width: '140px'}},
    {key: 'industry_name', label: '캠페인'},
    {key: 'status_code', label: '상태', thStyle: {width: '100px'}},
    {key: 'member', label: '참여자 관리', thStyle: {width: '92px'}},
];

export default  {
    name: 'AdminCompanyAssessmentInfo',
    components: {
        AssessmentMemberModal
    },
    mixins: [
        assessmentCommonMixin  
    ],
    props: {
        selected_company: {
            type: Object,
            default: () => ({})
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        // 기업 상세 라우트에서만 true — 열린 참여자 창을 ?members=<request_id> 로 남긴다 (목록 위 모달에서 쓰면 목록이 재조회됨)
        syncRoute: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            loadingRequestId: null,
        };
    },
    computed: {
        getEmptyTableHTML() {
            return getEmptyTableHTML('조회된 평가 정보가 없습니다.', '521px');
        },
        tableColumns() {
            return columns.map(column => ({...column, label: this.$t(column.label)}));
        },
    },
    mounted() {
        // ?members= 로 들어오면 그 프로젝트의 참여자 창을 연다. 없는 신청 건이면 키를 지운다
        if (!this.syncRoute || this.$route.query.members == null) return;
        const project = (this.selected_company.projects || []).find(p => String(p.request_id) === String(this.$route.query.members));
        if (project) {
            this.manageProjectMember(project);
        } else {
            this.setMembersQuery(null);
        }
    },
    methods: {
        progressLabel(item) {
            const status = this.getProgressStatus(item);
            return PROGRESS_STATUS_LABELS[status] || status;
        },
        async manageProjectMember (project) {
            if (this.loadingRequestId !== null) return;
            this.loadingRequestId = project.request_id;
            try {
                const opened = await this.$refs['assessment-member-modal'].manageProjectMember(project);
                if (this.syncRoute) this.setMembersQuery(opened ? project.request_id : null);
            } finally {
                this.loadingRequestId = null;
            }
        },
        onMemberModalClosed() {
            if (this.syncRoute) this.setMembersQuery(null);
        },
        setMembersQuery(requestId) {
            const {members, ...rest} = this.$route.query;
            const next = requestId != null ? String(requestId) : undefined;
            if (members === next) return;
            this.$router.replace({query: next ? {...rest, members: next} : rest}).catch(() => {});
        },
        // 옛 '확인하기'(enterProject) 와 같은 곳 — 프로젝트 진행현황
        progressRoute(project) {
            return { name: 'ProjectProgress', query: { client: project.client, pc: project.project_code, cmp: project.campaign_id, admin_mode: 'project' } };
        },
    }
};
</script>

<style lang="scss" scoped>
/* 고정 열 합 332(원청사 140 · 상태 100 · 행 아이콘 92) + 프로젝트 · 캠페인 ~194씩. 기업 상세 탭(1366 메뉴 펼침 표 폭 약 1038)과
   CompanyModal(xl — 992~1199 에서 표 폭 약 748) 안에 들어가게 — 그보다 좁으면 가로 스크롤 */
::v-deep .company-project-table {
    min-width: 720px;
}
</style>
